import events from 'events';
import SDK from '@ali/im-sdk-third';
import MessageLoading from '../../../../component/view/message_loading/';
import Parser from './parser';
import {
  cookie,
  consoleLog as log,
  Env,
  getClientType,
  removeTag,
  getUrlParam,
  Uploader,
  getAnonymousUserId,
} from '../../../../utils';
import { getOrderList } from '../../../../utils/request';
import { getFileInfo } from '../../../../utils/upload';
import ErrorCodes from '../../../const/errorCodes';

const DebugEnabled = Env.env !== 'live' || cookie.get('csc-debug') === '1';
const Configs = {
  stationType: 'xspace',
  msgHistoryPageSize: 20,
  apiMethod: 'JSONP',
  apiDebug: DebugEnabled,
  apiUserType: 1,
  timestampBlock: 300000, // 5 mins
};

export default class Agent extends events {
  constructor(app) {
    super();
    this.app = app;
    this.connected = false;
    this.queuing = 0;
    this.authInfo = null;
    this.imsid = app.param.imSid || app.appConfig.get('base').imsid;
    this.lastTime = 0;
    this.loggedIn = false;
    this.isBuyer = true;
    this.shownQueueNotice = false;
    this.uploadProvider = this.app.xspace.uploadProvider;

    const { cscAnonymous } = window.g_config || {};
    this.isAnonymous = cscAnonymous || getUrlParam('anonymous') === '1';

    this.__initSdk();

    if (this.uploadProvider === 'OSS') {
      this.uploader = new Uploader();
    }

    window.cscStore = window.cscStore || {};
    if (window.g_config && window.g_config.cscClient === 'seller') {
      this.isBuyer = false;
    }
  }

  online(params, newSession, showTransfering) {
    if (this.connected) return;

    const app = this.app;

    this.authInfo = params;

    if (showTransfering) {
      this.__addSystemMessge(Parser.transferAgent());
    }

    this.__disconnectedAgent();
    this.__login(newSession);

    app.view.setReceiver((data, type, show, post) => {
      this.__handleCustomerMessage(data, type, show, post);
    });
  }

  __initSdk() {
    const { xspace: { xspaceConfig: { appName, env, tenantId, apiHost } } } = this.app;
    const { apiMethod: method, apiDebug: debug, apiUserType: userType } = Configs;
    const clientType = getClientType();
    const sdkConfigs = {
      debug,
      method,
      env,
      appName,
      clientType,
      userType,
      tenantId,
      apiHost,
      delivered: true,
    };

    this.sdk = new SDK(sdkConfigs);
    log('init with:', sdkConfigs);

    if (this.uploadProvider === 'XSPACE') {
      this.uploader = this.sdk;
    }

    this.__listenEvents();

    // for test
    if (DebugEnabled) {
      window.xsdk = this.sdk;
    }
  }

  __showOrderSelector() {
    this.__showLoading();
    this.app.once('orderSelected', (orderNumber) => {
      window.Alime.cfg.onLiveAgent({
        orderNumber,
      }, (d) => { window.Alime.live(d); });
    });
    getOrderList().then((res) => {
      this.__hideLoading();
      const { data: { data: orders } = {} } = res;
      if (orders && orders.length) {
        this.app.appendMessage({
          type: 'orderSelector',
          body: {
            orders,
            subType: '_agent',
          },
        });
      } else {
        this.__createNewChat();
      }
    }, (err) => {
      this.__hideLoading();
      this.__createNewChat();
      console.log(err);
    });
  }

  __login(newSession) {
    const { buzUserId, userId, userName, tt, xcode, customerType } = this.authInfo;

    this.__showLoading();

    if (this.loggedIn) {
      this.__connect(newSession);
      return;
    }

    const loginData = { buzUserId, userId, userName, customerType, tt, xcode };
    if (this.isAnonymous) {
      loginData.frontEndUserId = getAnonymousUserId();
    }

    log('login with:', loginData);

    this.sdk.login(loginData).then((res) => {
      log('logged in:', res);

      if (res.timestamp) {
        this.loggedIn = true;
        this.__connect(newSession);
      } else {
        this.__handleError(res, ErrorCodes.LOGIN_FAILED);
        this.__switchToBot(this.sdkLoginFailed);
        this.sdkLoginFailed = true;
      }
    }, (e) => {
      this.__handleError(e, ErrorCodes.LOGIN_FAILED);
      this.__switchToBot(this.sdkLoginFailed);
      this.sdkLoginFailed = true;
    });
  }

  __handleResumeFailed(newSession) {
    if (newSession) {
      if (this.authInfo.orderSelectorDisabled || this.handledOrderSelector) {
        this.__createNewChat();
      } else if (!this.handledOrderSelector) {
        this.handledOrderSelector = true;

        if (window.Alime.app.windowVisible) {
          this.__showOrderSelector();
        } else {
          window.Alime.app.showOrderSelector = () => {
            this.__showOrderSelector();
          };
        }
      }
    } else {
      this.__switchToBot();
    }
  }

  __resume(newSession) {
    const { xspace: { xspaceConfig: { sourceId } } } = this.app;

    log('resume with:', { sourceId });
    this.sdk.resume({ sourceId }).then(
      (res) => {
        log('resume response:', res);
        if (res.isServicing) {
          log('resume successful');
          if (window.Alime.openAfterResume) {
            window.Alime.show();
            window.Alime.openAfterResume = false;
          }
          if (res.isInQueue) {
            this.__handleQueue();
          } else {
            this.__onConnected(res, true, true);
          }
        } else {
          this.__handleResumeFailed(newSession);
        }
      },
      (err) => {
        log('resume error', err);
        this.__handleResumeFailed(newSession);
      }
    );
  }

  __connect(newSession) {
    // after user login, have to resume first to restore other device session
    // after page loaded, we also try to resume to open the chat box if there is an existing session
    // when user switch from bot to agent, no need to resume
    // but just resume first
    this.__resume(newSession);
  }

  __dispatch() {
    const { imsid: outSessionId } = this;
    const { xspace: { xspaceConfig: { sourceId } } } = this.app;
    const { stationType } = Configs;
    const { skillGroupId, email, phone, language, orderId, customerType } = this.authInfo;
    const cusBuzInfo = {
      email,
      phone,
      language,
      orderId,
      customerType,
    };

    const params = {
      stationType,
      sourceId,
      skillGroupId,
      outSessionId,
      cusBuzInfo: JSON.stringify(cusBuzInfo),
    };

    log('dispatch with:', params);

    this.sdk.dispatch(params).then(
      (res) => {
        if (res.outSessionId) {
          log('dispatch successful');
          window.Alime.show();
          this.__onConnected(res);
        } else {
          this.__handleError(res, ErrorCodes.DISPATCH_FAILED);
          this.__switchToBot(
            this.app.chatOptions.chatWith !== 'agent',
            res && res.code === ErrorCodes.S_NO_AGENT_ONLINE
          );
        }
      },
      (e) => {
        this.__handleError(e, ErrorCodes.DISPATCH_FAILED);
        this.__switchToBot(
          this.app.chatOptions.chatWith !== 'agent',
          e && e.code === ErrorCodes.S_NO_AGENT_ONLINE
        );
      }
    );
  }

  __createNewChat() {
    const { skillGroupId } = this.authInfo;

    if (!skillGroupId) {
      this.__handleError(ErrorCodes.UNKNOWN_ERROR, ErrorCodes.NO_SKILLGROUP_ID);
      return;
    }

    log('check working time:', { skillGroupId });

    this.sdk
      .isWorking({
        skillGroupId,
      })
      .then(
        (res) => {
          log('working time: ', res);
          if (res.noAgentOnlineNote) {
            window.__cscMessages__['csc.error.no_agent_online'] = res.noAgentOnlineNote;
          }
          if (res.nonOperatingHourNote) {
            window.__cscMessages__['csc.error.out_working_time'] = res.nonOperatingHourNote;
          }
          if (res.isWorkingHour) {
            this.__dispatch();
          } else {
            this.__handleError(ErrorCodes.OUT_WORKING_TIME);
            this.__switchToBot(true, true);
          }
        },
        (err) => {
          this.__handleError(err, ErrorCodes.UNKNOWN_ERROR);
          this.__switchToBot(true);
        }
      );
  }

  __switchToBot(hideWelcome, hideAgentShortcut) {
    const { chatWith, onlyAgent } = this.app.chatOptions;
    if (chatWith === 'bot' || !onlyAgent) {
      log('switch to robot');
      this.__hideLoading();
      this.app.emit(this.app.events.NO_AGENT_SESSION, hideWelcome, hideAgentShortcut);
    }
  }

  __handleQueue(data) {
    const { skillGroupId } = this.authInfo;
    const { orderSelectorDisabled } = this.app.chatOptions;
    this.__disconnectedAgent();
    this.__hideLoading();

    if (data) {
      if (data.head) {
        const newQueue = parseInt(data.head.num, 10);

        if (newQueue && newQueue !== this.queuing) {
          this.queuing = newQueue;
          this.__addSystemMessge(Parser.queuing(newQueue));
        }
      }
    } else if (skillGroupId && orderSelectorDisabled) {
      log('get queue info with:', { skillGroupId });
      this.sdk.getQueueInfo({
        skillGroupId,
      }).then((res) => {
        log('queue info:', res);
        if (res && res.num) {
          this.__handleQueue({
            head: {
              num: res.num,
            },
          });
        }
      });
    }
  }

  __handleAgentTransfer(data) {
    if (data.type === 'leave') {
      this.__addSystemMessge(Parser.agentTransfer());
      this.__disconnectedAgent();
    }
    if (data.type === 'join') {
      this.__addSystemMessge(Parser.agentJoin(data.data));
    }
  }

  __listenEvents() {
    const { sdk } = this;

    sdk.on('Queue', (res) => {
      log('event Queue', res);
      this.__handleQueue(res);
    });

    sdk.on('UserJoin', (res) => {
      log('event UserJoin', res);
      this.__addSystemMessge(Parser.agentJoin(res.data));
    });

    sdk.on('message', (msgList) => {
      log('event message', msgList);
      msgList.forEach(msg => this.__handleAgentMessage(msg));
    });

    sdk.on('UserSwitch', (data) => {
      log('event UserSwitch', data);
      this.__handleAgentTransfer(data);
    });

    sdk.on('UserLeave', (res) => {
      log('event UserLeave', res);
      this.__addSystemMessge(Parser.agentLeft(res));
      this.__disconnectedAgent(true);
    });

    this.app.on(this.app.events.SUBMIT_FEEDBACK, (data) => {
      this.__submitFeedback(data);
    });

    this.app.on(this.app.events.AGENT_END_CHAT, (data) => {
      this.__endChatSession(data);
    });

    this.app.on(this.app.events.CANCEL_UPLOAD_FILE, (fileId) => {
      this.__cancelUpload(fileId);
    });
  }

  __handleError(code, customCode) {
    let error = code;

    if (error) {
      if (!error.code) {
        error = {
          code: error,
          customCode: customCode || error,
        };
      } else if (customCode) {
        error.customCode = customCode;
      }
    }

    log('error', error, JSON.stringify(error));
    this.__hideLoading();
    this.__addSystemMessge(Parser.errorMessage(error));
  }

  __onConnected(data, renderHistory, isResume) {
    log('connected:', data, JSON.stringify(data));

    this.agentTyping = new MessageLoading(this.app.view, 'agent');
    if (isResume) {
      this.__connectedAgent();
    }

    if (renderHistory) {
      this.sdk
        .getSessionLog({
          pageSize: Configs.msgHistoryPageSize,
        })
        .then((res) => {
          log('history: ', res, isResume);
          this.__renderHistory(res.messages, 0, isResume);
        });
    }
    this.__hideLoading();
  }

  __handleCustomerMessage(data, type, show, post) {
    log(data, type, show, post);
    switch (type) {
      case 'text':
        if (show) {
          this.__addChatMessage(Parser.customerMessage(data));
        }
        this.__sendMessage(data);
        break;
      case 'img':
        this.__uploadImage(data);
        break;
      case 'imgUrl':
        this.__sendImage(data);
        break;
      case 'file':
        this.__uploadFile(data);
        break;
      case 'photoWindvaneFailed':
        if (data && data.msg && data.msg === 'NO_PERMISSION') {
          this.__handleError(data.msg);
          return;
        }
        break;
      default:
    }
  }

  __handleAgentMessage(msg) {
    if (this.agentTyping) {
      this.agentTyping.remove();
    }

    switch (msg.cmd) {
      case 'PreMSG':
        if (msg.body) {
          this.agentTyping.init();
        }
        break;
      case 'MSG':
      case 'SOP':
        this.__addChatMessage(Parser.agentMessage(msg));
        break;
      case 'UserJoin':
        this.queuing = 0;
        this.__connectedAgent();
        break;
      case 'nonOfficeHourQueueNotice':
        if (!this.shownQueueNotice) {
          this.shownQueueNotice = true;
          this.__addSystemMessge(Parser.queueNotice());
        }
        break;
      default:
        break;
    }
  }

  __addSystemMessge(msg) {
    if (!msg) return;
    this.app.appendMessage({
      type: 'system',
      ...msg,
    });
  }

  __addChatMessage(msg) {
    if (msg.body && msg.body.text) {
      // this.__showTimestamp(msg.timestamp);
      this.app.appendMessage(msg);
    }
  }

  __renderHistory(messages = [], idx, isResume) {
    if (messages[idx]) {
      this.app.appendMessage(Parser.historyMessage(messages[idx])).then(() => {
        this.__renderHistory(messages, idx + 1, isResume);
      });
    } else if (isResume) {
      this.__addSystemMessge(Parser.resumeSuccessful());
    }
  }

  __showTimestamp(timestamp) {
    if (timestamp - this.lastTime > Configs.timestampBlock) {
      this.lastTime = timestamp;
      this.__addSystemMessge(Parser.timestamp(timestamp));
    }
  }

  __sendMessage(msg) {
    if (!this.connected) {
      this.__handleError(ErrorCodes.NOT_CONNECTED);
      return;
    }

    const message = removeTag(msg, true);
    if (!message) {
      return;
    }

    this.sdk
      .sendMessage('msg', {
        content: message,
      })
      .then(
        (res) => {
          log('message sent', res);
          if (!res.mid) {
            this.__handleError(res, ErrorCodes.SEND_MESSAGE_FAILED);
          }
          return res;
        },
        (e) => {
          this.__handleError(e, ErrorCodes.SEND_MESSAGE_FAILED);
        }
      );
  }

  __sendImage(url, file) {
    log(file);
    if (!url) {
      this.__handleError(ErrorCodes.SENT_IMAGE_FAILED);
      return;
    }
    this.__addChatMessage(Parser.customerImage(url));

    this.sdk.sendMessage('img', { url }).then(
      (res) => {
        log('Image sent', res);
      },
      (e) => {
        this.__handleError(e, ErrorCodes.SENT_IMAGE_FAILED);
      }
    );
  }

  __uploadImage(input) {
    const file = input.files[0];

    input.value = '';
    if (!this.connected) {
      this.__handleError(ErrorCodes.NOT_CONNECTED);
      return;
    }

    if (!/\.(gif|jpg|jpeg|png)$/i.test(file.name)) {
      this.__handleError(ErrorCodes.INVALID_IMAGE_FILE);
      return;
    }

    this.uploader.uploadFile(file).then(
      (res) => {
        const { url: fileUrl } = res;
        log('Image uploaded', res);

        if (fileUrl) {
          this.__sendImage(fileUrl, file);
        } else {
          this.__handleError(res, ErrorCodes.UPLOAD_FAILED);
        }
      },
      (e) => {
        this.__handleError(e, ErrorCodes.UPLOAD_FAILED);
      }
    );
  }

  __sendFile(data) {
    const { url, fileType, fileName, fileSize } = data;
    log('Try to send file', {
      url,
      fileType,
      fileSize,
      fileName,
    });

    this.sdk
      .sendMessage('file', {
        url,
        fileType,
        fileSize,
        fileName,
      })
      .then(
        (res) => {
          log('File sent', res);
        },
        (e) => {
          this.__handleError(e, ErrorCodes.SENT_FILE_FAILED);
        }
      );
  }

  __uploadFile(input) {
    const file = input.files[0];
    let fileMessage = null;

    input.value = '';
    if (!this.connected) {
      this.__handleError(ErrorCodes.NOT_CONNECTED);
      return;
    }

    if (!/\.(pdf|doc|docx|xls|xlsx)$/i.test(file.name)) {
      this.__handleError(ErrorCodes.INVALID_DOC_FILE);
      return;
    }

    if (this.uploadProvider === 'XSPACE') {
      fileMessage = Parser.customerFile(getFileInfo(file));
      log(fileMessage);
      fileMessage.body.disableCancelUpload = true;
      this.app.appendMessage(fileMessage);
    }

    this.uploader.uploadFile(
      file,
      {},
      {
        onStart: (fileInfo) => {
          this.app.appendMessage(Parser.customerFile(fileInfo));
        },
        onProgress: (event) => {
          if (fileMessage && fileMessage.body.fileId) {
            event.fileId = fileMessage.body.fileId;
          }
          this.app.emit(this.app.events.UPLOADING_FILE, event);
        },
      }
    ).then(
      (res) => {
        const { url } = res;
        log('File uploaded', res);

        if (url) {
          log(url);
          if (fileMessage && fileMessage.body.fileId) {
            const { fileId, fileType, fileName, fileSize } = fileMessage.body;
            res.fileId = fileId;
            res.fileType = fileType;
            res.fileName = fileName;
            res.fileSize = fileSize;
          }
          this.app.emit(this.app.events.FINISH_UPLOAD_FILE, res);
          this.__sendFile(res);
        } else {
          this.__handleError(res, ErrorCodes.UPLOAD_FAILED);
        }
      },
      (e) => {
        this.__handleError(e, ErrorCodes.UPLOAD_FAILED);
      }
    );
  }

  __cancelUpload(fileId) {
    if (this.uploader.abortUpload(fileId)) {
      log('cancel upload successful');
      this.app.emit(this.app.events.REMOVE_CANCELLED_FILE, fileId);
    } else {
      log('file uploaded, cannot cancel');
    }
  }

  __showLoading() {
    this.app.view.body.showLoader();
  }

  __hideLoading() {
    this.app.view.body.hideLoader();
  }

  __disconnectedAgent(endConversation) {
    this.connected = false;
    this.app.view.footer.blockChatAgent();
    this.app.view.footer.hideShortcuts();
    this.app.view.footer.emit('resize');

    if (this.isBuyer && endConversation && getUrlParam('csat') === '1') {
      this.__endConversation();
    }
  }

  __endConversation() {
    log('get evaluation data');
    this.sdk.createEvaluation().then(
      (res) => {
        if (res) {
          log('get evaluation successful', res);
          this.feedbackQuestion = Parser.feedback(res);

          log('parsed evaluation', this.feedbackQuestion);
          window.cscStore.feedback = this.feedbackQuestion;
          this.app.emit(this.app.events.SHOW_FEEDBACK);
        } else {
          this.__handleError(ErrorCodes.GET_EVALUATION_FAILED);
        }
      },
      (e) => {
        this.__handleError(e, ErrorCodes.GET_EVALUATION_FAILED);
      }
    );
  }

  __submitFeedback(feedback) {
    log('avaluation feedback', feedback);
    this.feedbackAnswer = Parser.submitFeedback(feedback, this.feedbackQuestion);

    log('submit evaluation', this.feedbackAnswer);
    this.sdk.submitEvaluation(this.feedbackAnswer).then(
      (res) => {
        log('submit evaluation successful', res);
      },
      (e) => {
        this.__handleError(e, ErrorCodes.SUBMIT_EVALUATION_FAILED);
      }
    );
    this.app.emit(this.app.events.DONE_FEEDBACK);
  }

  __connectedAgent() {
    this.connected = true;
    this.app.view.footer.unblockChatAgent();
    this.app.shortcuts.switchToAgent();
    this.app.view.footer.showShortcuts(true);
    setTimeout(() => {
      this.app.view.body.scrollToBottom();
    }, 500);
  }

  __endChatSession() {
    this.sdk.closeSession();
    this.app.view.footer.hideEndConversationHolder();
    this.app.view.footer.hideShortcuts();
  }
}



// WEBPACK FOOTER //
// ./src/app/context/channel/agent/index.js